/**
 * RESPONSIBILITY: the sermons the pastor has entered, and the lists they are filed under.
 *
 * Stored here rather than read from YouTube, because the notes, the questions, the
 * series and the speaker exist nowhere else (R66, R67). YouTube still supplies each
 * sermon's video and picture, and its newest uploads are shown beside these until they
 * are entered — see SermonsYoutube.
 *
 * Series, speakers and special events are lists an admin grows here, and a sermon picks
 * from them (R69) — the same arrangement as an event's tags.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Num, List, StringList } from '@framework/core/decorators';
import { SectionName } from '../section-names';
import { SermonRow } from './rows/sermon';
import { withUrls } from '../events/event-url';

const WORDS = 'Words on the page';

@Section({ name: SectionName.Sermons, label: 'Sermons' })
export class SermonsSection {
  @List({ label: 'Sermons', of: () => SermonRow, maxItems: 600 })
  sermons!: SermonRow[];

  @StringList({ label: 'Series', maxItems: 200, description: 'Every series a sermon can belong to.' })
  series!: string[];

  @StringList({ label: 'Speakers', maxItems: 100, description: 'Everyone who may be named as a speaker.' })
  speakers!: string[];

  @StringList({
    label: 'Special events', maxItems: 100,
    description: 'Occasions a sermon can be filed under — e.g. Easter, Christmas, the 25th Anniversary.',
  })
  occasions!: string[];

  /** Unique addresses are a fact about the LIST, so they are made here and not on the row. */
  static prepare(next: Record<string, unknown>, previous: Record<string, unknown> | undefined) {
    const rows = (next.sermons ?? []) as SermonRow[];
    const before = previous?.sermons as SermonRow[] | undefined;
    return { ...next, sermons: withUrls(rows, before) };
  }

  @Num({
    label: 'How many to show', min: 3, max: 60, integer: true, default: 12,
    group: WORDS, collapsed: true,
    description: 'The newest this many, until somebody searches or filters.',
  })
  latestCount!: number;

  @Text({ label: 'Title over the sermons', maxLength: 120, default: 'Latest', group: WORDS })
  latestHeading!: string;

  @Text({ label: 'Search box', maxLength: 120, default: 'Search sermons, scripture or speakers', group: WORDS })
  searchLabel!: string;

  @Text({ label: 'All series', maxLength: 60, default: 'All series', group: WORDS })
  allSeries!: string;

  @Text({ label: 'All speakers', maxLength: 60, default: 'All speakers', group: WORDS })
  allSpeakers!: string;

  @Text({ label: 'All years', maxLength: 60, default: 'All years', group: WORDS })
  allYears!: string;

  @Text({ label: 'All special events', maxLength: 60, default: 'All special events', group: WORDS })
  allOccasions!: string;

  @Text({ label: 'Clear the filters', maxLength: 60, default: 'Clear', group: WORDS })
  clearLabel!: string;

  @Text({
    label: 'When nothing matches', maxLength: 300, group: WORDS,
    default: 'No sermons match. Try fewer filters, or a shorter search.',
  })
  noMatch!: string;

  @Text({
    label: 'Over YouTube’s newest', maxLength: 120, default: 'Also on YouTube', group: WORDS,
    description: 'Videos on the channel not yet entered here. Empty hides them.',
  })
  youtubeHeading!: string;

  @Text({ label: 'Over the notes', maxLength: 120, default: 'Sermon notes', group: WORDS })
  notesHeading!: string;

  @Text({
    label: 'Over the questions', maxLength: 120, group: WORDS,
    default: 'Application & discussion questions',
  })
  questionsHeading!: string;

  @Text({ label: 'Over the rest of the series', maxLength: 120, default: 'More from this series', group: WORDS })
  moreHeading!: string;

  @Text({ label: 'Watch on YouTube', maxLength: 60, default: 'Watch on YouTube', group: WORDS })
  watchLabel!: string;

  @Text({ label: 'Back to all sermons', maxLength: 60, default: 'All sermons', group: WORDS })
  backLabel!: string;

  @Text({ label: 'When a sermon is not found', maxLength: 300, default: 'That sermon could not be found.', group: WORDS })
  missingText!: string;
}
